/**
 * SiteSettingsContext
 * C1.1 - Site Settings Standard Alignment
 *
 * Provides centralized access to site settings across the application.
 * All branding, contact info, and configuration come from this context.
 *
 * Dynamic theming: converts admin-configured hex colors into HSL triplets and
 * injects them into `document.documentElement` as CSS variables so that Tailwind
 * tokens (bg-accent, text-primary, ring-*) reflect the latest branding.
 *
 * Asset URLs (logo, favicon) are normalised to absolute URLs and cache-busted
 * using the settings `updated_at` timestamp so the browser shows the latest
 * uploaded asset without a hard refresh.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { sharedGet } from '../lib/api';
import { setAdminOverrides } from '../design-system/resolve';
import { designSystemAPI } from '../lib/api';
import { setActiveLanguages, setSettingsDefaultLanguage } from '../i18n/languageManager';

// Default settings (used before API loads)
const DEFAULT_SETTINGS = {
  site_name: 'SnappiPit',
  company_name: 'SnappiPit',
  logo_url: null,
  footer_logo_url: null,
  favicon_url: null,
  header_background_color: null,
  primary_color: '#f97316',
  secondary_color: '#1e293b',
  accent_color: '#f97316',
  // Granular theme controls (null → fall back to theme tokens)
  header_link_color: null,
  header_link_hover_color: null,
  footer_link_color: null,
  footer_link_hover_color: null,
  primary_button_color: null,
  primary_button_hover_color: null,
  secondary_button_color: null,
  secondary_button_hover_color: null,
  cta_button_color: null,
  cta_button_hover_color: null,
  tagline: 'Vehicle Service Marketplace',
  footer_text: 'SnappiPit - Connecting vehicle owners with trusted service providers',
  support_email: 'support@snappipit.com',
  contact_phone: null,
  default_country: 'AE',
  default_language: 'en',
  active_languages: ['en'],
  currency_symbol: 'AED',
  maintenance_mode: false,
  require_email_verification: true,
  updated_at: null,
  design_system: null,
  capabilities: {
    marketplace_mode: 'eoi',
    job_management_enabled: false,
    transactional_parts_enabled: false,
    bookings_enabled: false,
    provider_subscriptions_enabled: true,
    vehicle_care_enabled: true,
  },
};

const SiteSettingsContext = createContext(DEFAULT_SETTINGS);

// --------- Asset URL utilities ---------

/**
 * Resolve an asset URL returned by the API to an absolute URL that the
 * browser can fetch regardless of deployment layout.
 *
 *  - `null` / empty          -> null
 *  - `http(s)://…`           -> returned as-is
 *  - `/api/…` or `/uploads/…`-> prefixed with REACT_APP_BACKEND_URL
 *  - anything else           -> returned as-is
 *
 * Optionally appends a `?v=<version>` cache-buster so browsers pick up
 * newly-uploaded logos/favicons without a manual refresh.
 */
function resolveAssetUrl(u, version) {
  if (!u) return null;
  const s = String(u).trim();
  if (!s) return null;

  let abs;
  if (/^https?:\/\//i.test(s)) {
    abs = s;
  } else if (s.startsWith('/')) {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    abs = `${base}${s}`;
  } else {
    abs = s;
  }

  if (!version) return abs;
  const sep = abs.includes('?') ? '&' : '?';
  return `${abs}${sep}v=${encodeURIComponent(version)}`;
}

// --------- Color utilities ---------

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

function parseHex(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const num = parseInt(h, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function hexToHslTriplet(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d) + (g < b ? 6 : 0); break;
      case g: h = ((b - r) / d) + 2; break;
      default: h = ((r - g) / d) + 4;
    }
    h *= 60;
  }
  return `${clamp(Math.round(h), 0, 360)} ${clamp(Math.round(s * 100), 0, 100)}% ${clamp(Math.round(l * 100), 0, 100)}%`;
}

function foregroundFor(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return '0 0% 100%';
  const y = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return y > 160 ? '0 0% 9%' : '0 0% 100%';
}

function applyBrandingToDocument({ primary_color, secondary_color, accent_color }) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const accentHex = primary_color || accent_color;
  if (accentHex) {
    const hsl = hexToHslTriplet(accentHex);
    if (hsl) {
      root.style.setProperty('--accent', hsl);
      root.style.setProperty('--ring', hsl);
      root.style.setProperty('--accent-foreground', foregroundFor(accentHex));
      root.style.setProperty('--chart-1', hsl);
    }
  }
  if (secondary_color) {
    const hsl = hexToHslTriplet(secondary_color);
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--primary-foreground', foregroundFor(secondary_color));
    }
  }
}

/**
 * Inject a one-time <style> block holding the dynamic-branding rules. Each rule
 * references a CSS var with a fallback equal to the element's original token, so
 * when an admin has NOT configured a color the appearance is identical to before.
 * `applyGranularBranding` only sets the vars when colors are configured.
 */
function ensureBrandStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('brand-dynamic')) return;
  const style = document.createElement('style');
  style.id = 'brand-dynamic';
  style.textContent = `
.brand-header-link{color:var(--header-link,hsl(var(--muted-foreground)));transition:color .2s}
.brand-header-link:hover{color:var(--header-link-hover,hsl(var(--foreground)))}
.brand-footer-link{color:var(--footer-link,hsl(var(--primary-foreground)/0.7));transition:color .2s}
.brand-footer-link:hover{color:var(--footer-link-hover,hsl(var(--accent)))}
.brand-cta{background-color:var(--cta,hsl(var(--accent)));color:hsl(var(--accent-foreground))}
.brand-cta:hover{background-color:var(--cta-hover,hsl(var(--accent)/0.9))}
.bg-primary:hover{background-color:var(--primary-hover,hsl(var(--primary)/0.9))}
.bg-secondary:hover{background-color:var(--secondary-hover,hsl(var(--secondary)/0.8))}
`;
  document.head.appendChild(style);
}

/** Apply the 10 granular theme controls. Unsets vars when a color is cleared. */
function applyGranularBranding(s) {
  if (typeof document === 'undefined') return;
  ensureBrandStyle();
  const root = document.documentElement;
  const setOrClear = (varName, hex) => {
    if (hex) root.style.setProperty(varName, hex);
    else root.style.removeProperty(varName);
  };
  setOrClear('--header-link', s.header_link_color);
  setOrClear('--header-link-hover', s.header_link_hover_color);
  setOrClear('--footer-link', s.footer_link_color);
  setOrClear('--footer-link-hover', s.footer_link_hover_color);
  setOrClear('--cta', s.cta_button_color);
  setOrClear('--cta-hover', s.cta_button_hover_color);
  // Button base colors override the Tailwind HSL tokens (explicit wins).
  if (s.primary_button_color) {
    const hsl = hexToHslTriplet(s.primary_button_color);
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--primary-foreground', foregroundFor(s.primary_button_color));
    }
  }
  if (s.secondary_button_color) {
    const hsl = hexToHslTriplet(s.secondary_button_color);
    if (hsl) {
      root.style.setProperty('--secondary', hsl);
      root.style.setProperty('--secondary-foreground', foregroundFor(s.secondary_button_color));
    }
  }
  setOrClear('--primary-hover', s.primary_button_hover_color);
  setOrClear('--secondary-hover', s.secondary_button_hover_color);
}

/**
 * P8-P1.3 — surface-level theme overrides.
 *
 * Injected into a dedicated `<style id="ds-surfaces">` block that is populated
 * ONLY with the surface tokens an admin has actually set (and only when the
 * Design System is enabled). When nothing is set the block is empty → ZERO
 * rules → the shared primitives keep their Tailwind utilities (and any per-
 * instance className overrides) untouched. Hook classes (`ds-card`,
 * `ds-table-head`, `ds-table-row`, `ds-nav-link`, `ds-nav-active`) live on the
 * primitives and match nothing until a rule exists.
 */
function ensureSurfaceStyle() {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById('ds-surfaces');
  if (!el) {
    el = document.createElement('style');
    el.id = 'ds-surfaces';
    document.head.appendChild(el);
  }
  return el;
}

function buildSurfaceCss(surface) {
  if (!surface) return '';
  const rules = [];
  const decl = (sel, pairs) => {
    const body = pairs.filter(Boolean).join(';');
    if (body) rules.push(`${sel}{${body}}`);
  };
  const card = surface.card || {};
  decl('.ds-card', [
    card.bg && `background-color:${card.bg}`,
    card.border && `border-color:${card.border}`,
    card.radius && `border-radius:${card.radius}`,
    card.shadow && `box-shadow:${card.shadow}`,
  ]);
  const table = surface.table || {};
  decl('.ds-table-head', [
    table.header_bg && `background-color:${table.header_bg}`,
    table.header_fg && `color:${table.header_fg}`,
  ]);
  if (table.row_hover) rules.push(`.ds-table-row:hover{background-color:${table.row_hover}}`);
  const nav = surface.nav || {};
  decl('.ds-nav-link.ds-nav-active', [
    nav.active_bg && `background-color:${nav.active_bg}`,
    nav.active_fg && `color:${nav.active_fg}`,
  ]);
  decl('.ds-nav-link:hover:not(.ds-nav-active)', [
    nav.hover_bg && `background-color:${nav.hover_bg}`,
    nav.hover_fg && `color:${nav.hover_fg}`,
  ]);
  return rules.join('\n');
}

/**
 * P8-P1.4 — runtime brand-asset side effects.
 *
 * Injects/updates the social-share <meta> tags from
 * `design_system.brand_assets.og_image_url`. Asset-driven (NOT gated by the
 * theme `enabled` switch — assets apply whenever set, like logos/favicon).
 * Behaviour-neutral when no og image is set (tags removed → none present today).
 * Build-time assets (launcher icons / native splash) and hero_image are stored
 * only and intentionally have NO runtime effect here.
 */
function applyBrandAssets(brandAssets, version) {
  if (typeof document === 'undefined') return;
  const ogUrl = resolveAssetUrl(brandAssets && brandAssets.og_image_url, version);

  const upsertMeta = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!content) { if (el && el.dataset.ds === '1') el.remove(); return; }
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      el.dataset.ds = '1';
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };
  upsertMeta('property', 'og:image', ogUrl);
  upsertMeta('name', 'twitter:image', ogUrl);
  if (ogUrl) upsertMeta('name', 'twitter:card', 'summary_large_image');
  else upsertMeta('name', 'twitter:card', null);
}

/**
 * P8-P1.1/.2/.3 — hydrate the Design System admin override layer.
 *
 * BEHAVIOUR-NEUTRAL: only acts when `design_system.enabled === true` AND token
 * deltas are supplied. On a clean install (null / disabled) it clears the engine
 * override + removes the non-colour token vars (fonts/radius/shadow/space) so they
 * fall back to index.css / tokens.js (pixel-identical to today).
 *
 * Colours are applied **set-only** (never cleared here) so the existing branding
 * pipeline (`applyGranularBranding`) is never clobbered — when the Design System
 * supplies a colour it overrides (higher precedence, this effect runs after
 * branding); when it doesn't, the branding/default colour stands.
 */
function applyDesignSystem(ds) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const theme = ds && ds.enabled && ds.theme ? ds.theme : null;

  // 1) Resolution engine: hydrate (or clear) the admin override layer.
  setAdminOverrides(theme);

  // 2) Non-colour tokens: set when present, else clear → index.css default.
  const setOrClear = (name, val) => {
    if (val) root.style.setProperty(name, val);
    else root.style.removeProperty(name);
  };
  const font = (theme && theme.font) || {};
  setOrClear('--font-sans', font.sans);
  setOrClear('--font-heading', font.heading);
  setOrClear('--font-mono', font.mono);
  setOrClear('--radius', theme && theme.radius ? theme.radius.base : undefined);
  setOrClear('--space-unit', theme && theme.space ? theme.space.unit : undefined);
  const shadow = (theme && theme.shadow) || {};
  setOrClear('--shadow-sm', shadow.sm);
  setOrClear('--shadow', shadow.DEFAULT);
  setOrClear('--shadow-md', shadow.md);
  setOrClear('--shadow-lg', shadow.lg);

  // 3) Colours: set-only (hex → HSL triplet), with auto foreground.
  const color = (theme && theme.color) || {};
  const setColor = (name, hex) => {
    if (!hex) return;
    const hsl = hexToHslTriplet(hex);
    if (hsl) root.style.setProperty(name, hsl);
  };
  const setColorWithFg = (name, fgName, hex) => {
    if (!hex) return;
    const hsl = hexToHslTriplet(hex);
    if (hsl) {
      root.style.setProperty(name, hsl);
      root.style.setProperty(fgName, foregroundFor(hex));
    }
  };
  setColor('--background', color.background);
  setColor('--foreground', color.foreground);
  setColorWithFg('--primary', '--primary-foreground', color.primary);
  setColorWithFg('--secondary', '--secondary-foreground', color.secondary);
  setColorWithFg('--card', '--card-foreground', color.card);
  setColorWithFg('--destructive', '--destructive-foreground', color.destructive);
  setColor('--muted', color.muted);
  setColor('--border', color.border);
  setColor('--input', color.border);
  // Accent drives ring + chart-1 (mirrors the existing branding behaviour).
  if (color.accent) {
    const hsl = hexToHslTriplet(color.accent);
    if (hsl) {
      root.style.setProperty('--accent', hsl);
      root.style.setProperty('--ring', hsl);
      root.style.setProperty('--accent-foreground', foregroundFor(color.accent));
      root.style.setProperty('--chart-1', hsl);
    }
  }

  // 4) Surfaces (cards/tables/navigation): conditionally-populated style block.
  const surfaceEl = ensureSurfaceStyle();
  if (surfaceEl) surfaceEl.textContent = theme && theme.surface ? buildSurfaceCss(theme.surface) : '';
}


/**
 * Ensure a <link> element with the given rel exists in <head> and point its
 * href at the supplied URL. Covers all common favicon rels so browsers and
 * iOS/Android home-screen icons all update.
 */
function setIconLink(rel, href, type) {
  if (typeof document === 'undefined' || !href) return;
  let link = document.querySelector(`link[rel='${rel}']`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  if (type) link.type = type;
  link.href = href;
}

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // P8-P1.6: scoped preview — when ?ds_preview is present AND the viewer is an
  // authed admin, this holds the DRAFT design_system applied to THIS tab only.
  const [previewDS, setPreviewDS] = useState(null);

  // Apply default branding immediately on mount so the UI isn't flash-reset
  // before the API responds.
  useEffect(() => {
    applyBrandingToDocument(DEFAULT_SETTINGS);
    ensureBrandStyle();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await sharedGet('/settings/public', {}, 60_000);
        const data = response.data || {};
        const version = data.updated_at || '';

        // P9-P1: hydrate the i18n language manager from public settings. Active
        // languages default to ['en'] → app stays English-only (clamped) until
        // Arabic is activated here.
        setSettingsDefaultLanguage(data.localization?.default_language || 'en');
        setActiveLanguages(data.localization?.active_languages || ['en']);

        // Map the API response to our standardized flat structure
        const normalized = {
          site_name: data.platform?.site_name || DEFAULT_SETTINGS.site_name,
          company_name: data.platform?.site_name || DEFAULT_SETTINGS.company_name,
          logo_url: resolveAssetUrl(data.platform?.logo_url, version),
          footer_logo_url: resolveAssetUrl(data.platform?.footer_logo_url, version),
          favicon_url: resolveAssetUrl(data.platform?.favicon_url, version),
          header_background_color: data.platform?.header_background_color || null,
          primary_color: data.branding?.primary_color || DEFAULT_SETTINGS.primary_color,
          secondary_color: data.branding?.secondary_color || DEFAULT_SETTINGS.secondary_color,
          accent_color: data.branding?.accent_color || DEFAULT_SETTINGS.accent_color,
          header_link_color: data.branding?.header_link_color || null,
          header_link_hover_color: data.branding?.header_link_hover_color || null,
          footer_link_color: data.branding?.footer_link_color || null,
          footer_link_hover_color: data.branding?.footer_link_hover_color || null,
          primary_button_color: data.branding?.primary_button_color || null,
          primary_button_hover_color: data.branding?.primary_button_hover_color || null,
          secondary_button_color: data.branding?.secondary_button_color || null,
          secondary_button_hover_color: data.branding?.secondary_button_hover_color || null,
          cta_button_color: data.branding?.cta_button_color || null,
          cta_button_hover_color: data.branding?.cta_button_hover_color || null,
          tagline: data.platform?.tagline || DEFAULT_SETTINGS.tagline,
          footer_text: data.contact?.footer_text || DEFAULT_SETTINGS.footer_text,
          support_email: data.contact?.support_email || DEFAULT_SETTINGS.support_email,
          contact_phone: data.contact?.support_phone || null,
          default_country: data.localization?.default_country || DEFAULT_SETTINGS.default_country,
          default_language: data.localization?.default_language || DEFAULT_SETTINGS.default_language,
          active_languages: data.localization?.active_languages || DEFAULT_SETTINGS.active_languages,
          currency_symbol:
            data.localization?.currency_symbol ||
            data.localization?.base_currency ||
            DEFAULT_SETTINGS.currency_symbol,
          maintenance_mode: data.maintenance_mode || false,
          require_email_verification: data.require_email_verification !== false,
          enable_global_parts_sourcing: data.enable_global_parts_sourcing || false,
          design_system: data.design_system || null,
          capabilities: {
            ...DEFAULT_SETTINGS.capabilities,
            ...(data.capabilities || {}),
          },
          updated_at: version || null,
        };

        setSettings(normalized);
      } catch (err) {
        console.error('Failed to fetch site settings:', err);
        setError(err);
        // Keep default settings on error
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // P8-P1.6: preview mode — if ?ds_preview is present, try to load the DRAFT
  // (admin-only endpoint, uses the logged-in admin's token). Scoped to this tab;
  // live visitors without the flag (or without admin auth) are never affected.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const flag = new URLSearchParams(window.location.search).get('ds_preview');
    if (!flag) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await designSystemAPI.get();
        const draft = res.data?.draft;
        if (!cancelled && draft) {
          // Force enabled so the admin always sees the draft look while previewing.
          setPreviewDS({ ...draft, enabled: true });
        }
      } catch (e) {
        // Not an admin / not authed → ignore, show published (safe).
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-apply branding whenever the color values in settings change
  useEffect(() => {
    applyBrandingToDocument({
      primary_color: settings.primary_color,
      secondary_color: settings.secondary_color,
      accent_color: settings.accent_color,
    });
    applyGranularBranding(settings);
  }, [
    settings.primary_color, settings.secondary_color, settings.accent_color,
    settings.header_link_color, settings.header_link_hover_color,
    settings.footer_link_color, settings.footer_link_hover_color,
    settings.primary_button_color, settings.primary_button_hover_color,
    settings.secondary_button_color, settings.secondary_button_hover_color,
    settings.cta_button_color, settings.cta_button_hover_color,
  ]);

  // P8-P1.1+: hydrate the Design System override layer (behaviour-neutral when
  // disabled/empty). Preview draft (this tab) takes precedence over published.
  useEffect(() => {
    applyDesignSystem(previewDS || settings.design_system);
  }, [settings.design_system, previewDS]);

  // P8-P1.4: runtime brand assets (og:image meta). Asset-driven, ungated.
  useEffect(() => {
    const ds = previewDS || settings.design_system;
    applyBrandAssets(ds?.brand_assets, settings.updated_at);
  }, [settings.design_system, settings.updated_at, previewDS]);

  // Update document title & favicon when settings load/change
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (settings.site_name) {
      document.title = settings.tagline
        ? `${settings.site_name} - ${settings.tagline}`
        : settings.site_name;
    }

    if (settings.favicon_url) {
      // Point every common icon rel at the uploaded favicon so all browsers
      // + installed PWAs + mobile home-screen shortcuts pick it up.
      setIconLink('icon', settings.favicon_url);
      setIconLink('shortcut icon', settings.favicon_url);
      setIconLink('apple-touch-icon', settings.favicon_url);
    }
  }, [settings.site_name, settings.tagline, settings.favicon_url]);

  return (
    <SiteSettingsContext.Provider value={{ ...settings, loading, error, previewMode: Boolean(previewDS) }}>
      {previewDS && (
        <div
          data-testid="ds-preview-banner"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2147483647,
            background: '#1d4ed8', color: '#fff', padding: '6px 12px',
            fontSize: 12, fontWeight: 600, textAlign: 'center',
            fontFamily: 'system-ui, sans-serif', letterSpacing: '0.02em',
          }}
        >
          DESIGN SYSTEM PREVIEW (draft) — visible only to you. Live visitors see the published theme.
        </div>
      )}
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (context === undefined) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
};

export default SiteSettingsContext;
